import { useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button, MobileStepper,
  IconButton,
} from '@mui/material';
import { Close, ArrowForward, ArrowBack, CalendarMonth, Payment, Groups, Shield, Lightbulb } from '@mui/icons-material';
import { colors, designTokens } from '../../theme/theme';

interface Props {
  open: boolean;
  onClose: () => void;
}

const steps = [
  {
    icon: <CalendarMonth sx={{ fontSize: 48, color: colors.primary.main }} />,
    title: 'Browse Weekly Events',
    description: 'Every Monday, new events drop — professional discussions and social meetups at partner coffee shops across Baku. Pick a topic that excites you.',
    tip: 'Sign up by Wednesday midnight — that\'s when the table locks.',
  },
  {
    icon: <Payment sx={{ fontSize: 48, color: colors.primary.main }} />,
    title: 'Commit With a Coffee',
    description: 'Pre-pay for your coffee voucher (10-13 AZN). This isn\'t a fee — it\'s your drink. The commitment ensures everyone who signs up actually shows up.',
    tip: 'If the table doesn\'t fill (min 5 people), you get a full refund or a pivot to another event.',
  },
  {
    icon: <Groups sx={{ fontSize: 48, color: colors.primary.main }} />,
    title: 'Show Up & Connect',
    description: 'Walk into the partner cafe, scan your QR code, grab your coffee, and join 5 people at the CoffeeMates table. 90 minutes of real conversation.',
    tip: 'No awkward intros needed — the topic is the ice-breaker.',
  },
  {
    icon: <Shield sx={{ fontSize: 48, color: colors.primary.main }} />,
    title: 'You\'re Safe Here',
    description: 'CoffeeMates has a zero-tolerance policy for harassment. Every participant acknowledges our safety promise. We take it seriously.',
    tip: 'Report any issue — we act within 24 hours.',
  },
];

export default function OnboardingModal({ open, onClose }: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = steps.length;
  // eslint-disable-next-line security/detect-object-injection -- activeStep is bounded local state, 0..maxSteps-1
  const step = steps[activeStep];
  const isLast = activeStep === maxSteps - 1;

  const handleNext = () => {
    if (isLast) {
      localStorage.setItem('cm_onboarding_seen', 'true');
      onClose();
    } else {
      setActiveStep(s => s + 1);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: `${designTokens.radius.lg}px`, overflow: 'hidden' } }}>

      <IconButton
        aria-label="Close onboarding"
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ textAlign: 'center', py: 5, px: 4 }}>
        <Box sx={{ mb: 3 }}>{step.icon}</Box>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#FFFFFF' }}>
          {step.title}
        </Typography>

        <Typography variant="body1" sx={{ color: colors.neutral[900], mb: 2, lineHeight: 1.7 }}>
          {step.description}
        </Typography>

        {/* Coral subtle bg + primary.light text (contrast-safe); icon not
            emoji per DS emoji policy. */}
        <Box sx={{
          bgcolor: colors.primary.subtle,
          borderRadius: `${designTokens.radius.md}px`,
          p: 2, mb: 3,
          display: 'flex', alignItems: 'flex-start', gap: 1, textAlign: 'left',
        }}>
          <Lightbulb sx={{ color: colors.primary.light, fontSize: 18, mt: 0.25, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: colors.primary.light, fontStyle: 'italic' }}>
            {step.tip}
          </Typography>
        </Box>

        <Button
          variant="contained" size="large" fullWidth
          onClick={handleNext}
          endIcon={!isLast ? <ArrowForward /> : undefined}
          sx={{ py: 1.5, mb: 2 }}
        >
          {isLast ? 'Got It — Let\'s Go!' : 'Next'}
        </Button>

        <MobileStepper
          variant="dots"
          steps={maxSteps}
          position="static"
          activeStep={activeStep}
          sx={{ justifyContent: 'center', bgcolor: 'transparent', '& .MuiMobileStepper-dot': { mx: 0.5 } }}
          backButton={
            activeStep > 0 ? (
              <IconButton
                size="small"
                aria-label="Previous step"
                onClick={() => setActiveStep(s => s - 1)}
              >
                <ArrowBack />
              </IconButton>
            ) : <Box sx={{ width: 34 }} />
          }
          nextButton={<Box sx={{ width: 34 }} />}
        />

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {activeStep + 1} of {maxSteps}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
